import type { SVGProps } from 'react';
import { cn } from './cn.js';

/**
 * O conjunto de ícones da plataforma.
 *
 * Um componente só, porque a espessura do traço, o arredondamento das pontas e
 * o tamanho não são escolha de quem escreve a tela — são a assinatura do deck,
 * e assinatura que cada tela redesenha deixa de ser assinatura. `currentColor`
 * sempre: o ícone herda a cor de quem o contém e nunca carrega cor própria.
 *
 * Ícone que carrega informação nunca vai sozinho: ou acompanha texto, ou
 * recebe `rotulo`. Sem uma das duas coisas, quem usa leitor de tela não recebe
 * nada — que é o motivo de `aria-hidden` ser o padrão aqui.
 */
export type IconName =
  | 'confirmado'
  | 'bloqueado'
  | 'atencao'
  | 'informacao'
  | 'busca'
  | 'sair'
  | 'pessoa'
  | 'menu'
  | 'fechar'
  | 'ponto'
  | 'seta-esquerda'
  | 'seta-direita'
  | 'seta-baixo'
  | 'seta-cima'
  | 'camera'
  | 'documento'
  | 'clipe'
  // os oito do rail: um por módulo, e nenhum deles usado em outro lugar
  | 'inicio'
  | 'demandas'
  | 'cobranca'
  | 'comercial'
  | 'gestao'
  | 'retencao'
  | 'erp'
  | 'plataforma'
  /** O guincho da Assistência 24h (21/10/2026): o caminhão com o braço do reboque. */
  | 'assistencia'
  /*
   * Os do item de menu, portados do desenho de 05/09.
   *
   * A lateral nova mostra os ~29 itens de uma vez, agrupados por módulo, e no
   * desenho cada item leva o seu ícone — é o que deixa a coluna varrível sem
   * ler palavra por palavra. Os traçados são os do arquivo do desenho, não
   * redesenhados: eles são a assinatura que o resto da tela combina.
   *
   * Repetição é permitida aqui e não no rail. No rail o ícone aparece SOZINHO e
   * dois iguais tornam dois módulos indistinguíveis; no item ele vem sempre com
   * o rótulo ao lado, e `refresh` servir a "Esteira" e a "Saúde da integração"
   * só diz que as duas telas falam de sincronizar.
   */
  | 'relogio'
  /** O gatilho do calendário do `DateField` — o par do `relogio` no `TimeField`. */
  | 'calendario'
  /** O telefone de contato, na célula de tabela — a pessoa sem documento se acha por ele. */
  | 'telefone'
  /** O e-mail de contato — o par do `telefone` na mesma tabela. */
  | 'email'
  /**
   * O terceiro do sinistro: DUAS pessoas inteiras, do mesmo tamanho. O par do
   * `pessoa`, que é o associado. `pessoas` (uma cabeça e um recorte atrás) lia
   * como uma pessoa só a 16px, e na tabela as duas linhas pareciam iguais —
   * medido no navegador em 25/09/2026. `pessoas` continua como está: ele quer
   * dizer "um grupo" em vinte outros lugares.
   */
  | 'terceiro'
  | 'livro'
  | 'carrinho'
  | 'etiqueta'
  | 'pessoas'
  | 'percentual'
  | 'codigo-barras'
  | 'dinheiro'
  | 'pix'
  | 'cartao'
  | 'atualizar'
  | 'quadro'
  | 'bandeira'
  | 'cracha'
  | 'grade'
  | 'grafico'
  | 'escudo'
  | 'chave'
  | 'tomada'
  | 'historico'
  | 'sol'
  | 'lua'
  /* Os dois da barra de ações: abrir um registro novo, e levar a lista embora. */
  | 'mais'
  /* Diminuir — o par do `mais` no zoom do diagrama do time (23/09/2026). */
  | 'menos'
  | 'baixar'
  /** A ação rápida de pré-visualizar um registro sem sair da lista. */
  | 'olho'
  /** O sino do menu superior — histórico de avisos. */
  | 'sino'
  /*
   * Os dois do PORTAL do associado, que a interna não usa.
   *
   * `veiculo` é o item "Meus veículos" do menu, e `copiar` é o botão que leva a
   * linha digitável do boleto para a área de transferência. Nenhum dos ~43
   * traçados existentes serve: `carrinho` é compra, `clipe` é anexo, e um
   * símbolo emprestado num menu de cinco itens é o tipo de economia que faz a
   * pessoa parar para decifrar.
   */
  | 'veiculo'
  | 'copiar'
  /*
   * Os três da TABELA, e nenhum deles servia emprestado.
   *
   * `reticencias` é o gatilho da coluna de ações: o desenho põe ali um alvo que
   * abre o que a linha permite fazer, e nenhum dos ~45 traçados diz "há mais
   * aqui" sem dizer outra coisa junto — `mais` é criar, `olho` é espiar.
   *
   * `ordenar` é a seta dupla do cabeçalho ordenável. Ela precisa ser UM símbolo
   * e não duas setas nossas empilhadas: `seta-cima` mais `seta-baixo` no mesmo
   * botão são dois alvos visuais para uma ação só, e em 13px viram um borrão.
   *
   * `filtro` é o funil da barra de ações. `busca` é a lupa e já tem dono — o
   * campo de texto —, e usar a mesma lupa nos dois põe dois gestos diferentes
   * atrás do mesmo desenho na mesma barra.
   */
  | 'reticencias'
  | 'ordenar'
  | 'filtro'
  /*
   * Os oito da GRADE DE ANEXOS (17/09), e o que cada um faz lá.
   *
   * `clipe` e `documento` já existiam e continuam com o dono que têm: o clipe é
   * o gesto de anexar, o documento é o arquivo que não tem prévia. O que
   * faltava era o vocabulário de dentro do ladrilho.
   *
   * - `nuvem` é o alvo vazio: "arraste um arquivo aqui". `baixar` tem a seta
   *   certa e o sentido trocado — ele é o botão que traz o arquivo para cá.
   * - `imagem`, `video` e `audio` são os três gêneros que a grade desenha
   *   quando a prévia ainda não carregou ou o navegador não sabe desenhá-la.
   *   Um ícone só para os três devolveria o quadrado cinza que a prévia existe
   *   para não ser.
   * - `tocar` é o distintivo sobre a miniatura do vídeo. Ele não é `video`: um
   *   diz "isto é um vídeo" e o outro diz "clique para assistir", e na mesma
   *   miniatura os dois aparecem juntos.
   * - `expandir` abre o visualizador; `mover` é a alça que reordena; `lixeira`
   *   remove. `fechar` não serve para remover — ele dispensa a sobreposição
   *   que está aberta, e os dois aparecem na MESMA tela quando o visualizador
   *   está de pé.
   */
  | 'nuvem'
  | 'imagem'
  | 'video'
  | 'audio'
  | 'tocar'
  | 'expandir'
  | 'mover'
  | 'lixeira'
  /*
   * Os três do PLAYER de áudio (18/09), e por que nenhum servia emprestado.
   *
   * `tocar` já existia e continua com o dono que tem: o triângulo sobre a
   * miniatura do vídeo e o de dentro do botão do player são o mesmo gesto. O
   * que faltava era o resto da barra.
   *
   * - `pausa` é o par de `tocar` no MESMO botão, e é o único caso em que dois
   *   traçados se alternam no lugar de um: o botão mostra o que o clique FAZ,
   *   não o que está acontecendo.
   * - `som` e `mudo` são o alto-falante com e sem as ondas. O X de `mudo` não é
   *   o de `fechar` nem o de `bloqueado` — ele fica ao lado do cone, e os três
   *   traçados são diferentes inteiros, que é o que `icons.test.ts` cobra.
   */
  | 'pausa'
  | 'som'
  | 'mudo'
  /**
   * O pino de localização, do campo de Endereço (19/09) — o gatilho que abre
   * o mapa para marcar latitude e longitude ao lado do CEP.
   */
  | 'mapa'
  /** A coroa sobre o 1º lugar do pódium do ranking (07/10) — e só ali. */
  | 'coroa'
  /*
   * Os dois da CALCULADORA do comercial (29/10/2026). `calculadora` é o título
   * da página; `tendencia` é a linha que sobe, da projeção de crescimento —
   * `grafico` são barras e diz "relatório", não "está subindo".
   */
  | 'calculadora'
  | 'tendencia'
  /*
   * Os serviços da Assistência 24h (29/10/2026). O ladrilho do tipo usava
   * `tomada` para bateria, `ponto` para pneu e `calendario` para hospedagem:
   * o desenho dizia outra coisa, e a central lia o rótulo em vez do ícone.
   * Cada serviço ganhou o objeto que ele atende — a bateria de carro, a roda,
   * a bomba de combustível, a casa, o táxi e a cama.
   */
  | 'bateria'
  | 'pneu'
  | 'combustivel'
  | 'casa'
  | 'taxi'
  | 'cama'
  /** A chave do chaveiro: argola, haste e dentes — a `chave` do menu é outro desenho. */
  | 'chaveiro'
  /** A base do prestador no mapa da rota: de onde ele sai e para onde volta. */
  | 'garagem';

/*
 * As chaves são os VALORES de `IconName`, não nomes nossos — o mapa é
 * indexado pela união literal, e traduzi-las quebra a indexação.
 */
// @valor
const STROKES: Record<IconName, string> = {
  confirmado: 'M4.5 12.5l5 5 10-11',
  /*
   * Círculo com barra, e não o X puro que estava aqui.
   *
   * O X puro era **o mesmo traçado de `fechar`**, caractere por caractere: o
   * selo de erro e o botão de fechar desenhavam o mesmo glifo. O `DESIGN.md`
   * pede "cor + símbolo + texto" justamente para o símbolo carregar sentido
   * sozinho, e um símbolo que também significa "dispensar isto" não carrega.
   * `icons.test.ts` cobra que nenhum par volte a coincidir.
   */
  bloqueado: 'M12 21a9 9 0 100-18 9 9 0 000 18zM5.6 5.6l12.8 12.8',
  atencao: 'M12 3.6L1.8 20.4h20.4L12 3.6zM12 10v4.4M12 17.6v.1',
  informacao: 'M12 21a9 9 0 100-18 9 9 0 000 18zM12 11v5M12 7.6v.1',
  busca: 'M11 18a7 7 0 100-14 7 7 0 000 14zM20 20l-4.1-4.1',
  sair: 'M14 20H6a2 2 0 01-2-2V6a2 2 0 012-2h8M17 15l4-3-4-3M21 12H10',
  pessoa: 'M12 12a4 4 0 100-8 4 4 0 000 8zM4.5 20a7.5 7.5 0 0115 0',
  menu: 'M4 7h16M4 12h16M4 17h16',
  fechar: 'M6 6l12 12M18 6L6 18',
  ponto: 'M12 17.5a5.5 5.5 0 100-11 5.5 5.5 0 000 11z',
  /*
   * A seta de voltar. Nasceu porque não existia: sete telas escreviam o
   * caractere `←` no meio do texto do link, e o `DESIGN.md` diz que glifo de
   * texto não é ícone — ele não tem a espessura de traço da família, não
   * herda o tamanho e, em fonte que não o traga, vira um retângulo vazio.
   */
  'seta-esquerda': 'M14.5 5.5L8 12l6.5 6.5',
  'seta-direita': 'M9.5 5.5l6.5 6.5-6.5 6.5',
  'seta-baixo': 'M5.5 9.5l6.5 6.5 6.5-6.5',
  'seta-cima': 'M5.5 14.5l6.5-6.5 6.5 6.5',

  /*
   * Os dois de anexo, acrescentados pela `DocumentCapture`.
   *
   * `camera` é o gatilho do controle; `documento` é o que ocupa o lugar da
   * prévia quando o arquivo é PDF — e ele existe justamente para NÃO ser um
   * quadrado cinza que a pessoa fica esperando carregar.
   */
  camera:
    'M4 8.5h3l1.5-2.5h7L17 8.5h3v10H4v-10zM12 16a3.2 3.2 0 100-6.4 3.2 3.2 0 000 6.4z',
  documento:
    'M13.5 3.5H7a1.5 1.5 0 00-1.5 1.5v14A1.5 1.5 0 007 20.5h10a1.5 1.5 0 001.5-1.5V8.5l-5-5zM13.5 3.5v5h5M9 13h6M9 16.5h6',

  /*
   * O clipe, acrescentado pelo anexo da demanda em 06/09.
   *
   * `documento` não servia: ele é o arquivo que JÁ existe, e aparece na lista de
   * anexos e no lugar da prévia do PDF. O gatilho que ANEXA é outro gesto, e um
   * ícone só para os dois deixaria o compositor com o mesmo desenho na barra de
   * ações e na linha logo acima dela.
   */
  clipe:
    'M19.5 11.5l-8 8a4.8 4.8 0 01-6.8-6.8l8.6-8.6a3.3 3.3 0 014.6 4.6l-8.6 8.6a1.7 1.7 0 01-2.4-2.4l7.9-7.9',

  /*
   * Os ícones do rail.
   *
   * No rail o ícone fica **sozinho**, sem rótulo ao lado — é a única parte da
   * interface onde isso acontece, e é o que torna a escolha do desenho uma
   * decisão de navegação e não de enfeite. Cada um recebe `rotulo`, que vira
   * nome acessível e dica em repouso; o desenho é a pista rápida para quem já
   * conhece, nunca a única.
   *
   * Silhuetas deliberadamente distintas entre si: casa, balão, cédula, barras,
   * organograma, escudo, encaixe, engrenagem. Dois ícones parecidos no rail
   * custam uma parada de leitura em toda troca de módulo.
   */
  inicio: 'M3.5 11L12 4l8.5 7M5.5 9.5V20h13V9.5',
  demandas: 'M20 15a2 2 0 01-2 2H8l-4 3.5V6a2 2 0 012-2h12a2 2 0 012 2v9z',
  cobranca: 'M3 6.5h18v11H3v-11zM12 15a2.5 2.5 0 100-5 2.5 2.5 0 000 5z',
  comercial: 'M4 20V10M10 20V4M16 20v-7M22 20H2',
  gestao: 'M12 3.5v4M6 20v-3.5M18 20v-3.5M6 16.5h12v-3H6v3zM12 13.5v-6',
  retencao: 'M12 3.5l7 3v5c0 4-3 7-7 8.5-4-1.5-7-4.5-7-8.5v-5l7-3z',
  erp: 'M9 4.5H5.5v15H9M15 4.5h3.5v15H15M9 9.5h6M9 14.5h6',
  plataforma:
    'M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 13.2l1.8 1.4-1.8 3.1-2.1-.8a7.5 7.5 0 01-2 1.2l-.3 2.2h-3.6l-.3-2.2a7.5 7.5 0 01-2-1.2l-2.1.8-1.8-3.1 1.8-1.4a7.5 7.5 0 010-2.4L3.2 9.4l1.8-3.1 2.1.8a7.5 7.5 0 012-1.2l.3-2.2h3.6l.3 2.2c.7.3 1.4.7 2 1.2l2.1-.8 1.8 3.1-1.8 1.4c.2.8.2 1.6 0 2.4z',
  assistencia:
    'M2.5 16.5V8.5h10v8M12.5 11H17l3.5 3.5v2h-2M7.5 16.5h7M5.5 18.5a2 2 0 100-4 2 2 0 000 4zM16.5 18.5a2 2 0 100-4 2 2 0 000 4zM5 8.5l4-5',

  /* Os do item de menu — traçados do arquivo do desenho, sem redesenhar. */
  relogio: 'M12 21a9 9 0 100-18 9 9 0 000 18M12 7v5l4 2',
  email: 'M3.5 6h17v12h-17zM3.5 6.5l8.5 6.5 8.5-6.5',
  terceiro:
    'M8 11.5a3.25 3.25 0 100-6.5 3.25 3.25 0 000 6.5zM16 11.5a3.25 3.25 0 100-6.5 3.25 3.25 0 000 6.5zM2 20a6 6 0 0112 0M14.6 14.2A6 6 0 0122 20',
  telefone:
    'M6.5 3.5h3l1.5 4-2 1.5a11 11 0 005 5l1.5-2 4 1.5v3a2 2 0 01-2 2A16.5 16.5 0 014.5 5.5a2 2 0 012-2z',
  calendario:
    'M8 3v4M16 3v4M4 10h16M6 5h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V7a2 2 0 012-2',
  livro: 'M5 4h6v16H5zM13 4h6v16h-6',
  carrinho: 'M4 5h2l2 10h10l2-7H7M9 20v.5M17 20v.5',
  etiqueta: 'M4 12 12 4h7v7l-8 8zM15 8v.5',
  pessoas:
    'M9 12a3.5 3.5 0 100-7 3.5 3.5 0 000 7M3 20c0-3 2.7-5 6-5s6 2 6 5M16 20c0-2.2-.8-3.9-2-5 2.8 0 5 2 5 5',
  percentual: 'M6 18 18 6M8 9a2 2 0 100-4 2 2 0 000 4M16 19a2 2 0 100-4 2 2 0 000 4',
  'codigo-barras': 'M4 5v14M8 5v14M11 5v14M14 5v10M17 5v14M20 5v14',
  dinheiro: 'M3 7h18v10H3zM12 15a3 3 0 100-6 3 3 0 000 6',
  /*
   * As duas formas da PI (19/10/2026). O Pix é o SÍMBOLO da marca, e não um
   * desenho nosso: um losango genérico não é reconhecido como Pix. O traçado é
   * o do Simple Icons (CC0), cheio e sem contorno — ver `BRAND`.
   */
  pix: 'M5.283 18.36a3.505 3.505 0 0 0 2.493-1.032l3.6-3.6a.684.684 0 0 1 .946 0l3.613 3.613a3.504 3.504 0 0 0 2.493 1.032h.71l-4.56 4.56a3.647 3.647 0 0 1-5.156 0L4.85 18.36ZM18.428 5.627a3.505 3.505 0 0 0-2.493 1.032l-3.613 3.614a.67.67 0 0 1-.946 0l-3.6-3.6A3.505 3.505 0 0 0 5.283 5.64h-.434l4.573-4.572a3.646 3.646 0 0 1 5.156 0l4.559 4.559ZM1.068 9.422 3.79 6.699h1.492a2.483 2.483 0 0 1 1.744.722l3.6 3.6a1.73 1.73 0 0 0 2.443 0l3.614-3.613a2.482 2.482 0 0 1 1.744-.723h1.767l2.737 2.737a3.646 3.646 0 0 1 0 5.156l-2.736 2.736h-1.768a2.482 2.482 0 0 1-1.744-.722l-3.613-3.613a1.77 1.77 0 0 0-2.444 0l-3.6 3.6a2.483 2.483 0 0 1-1.744.722H3.791l-2.723-2.723a3.646 3.646 0 0 1 0-5.156',
  cartao:
    'M5 5.8h14a1.5 1.5 0 011.5 1.5v9.4a1.5 1.5 0 01-1.5 1.5H5a1.5 1.5 0 01-1.5-1.5V7.3A1.5 1.5 0 015 5.8zM3.5 9.8h17M6.8 14.6h3.4',
  atualizar: 'M20 12a8 8 0 11-3-6.2M20 4v5h-5',
  quadro: 'M4 5h5v14H4zM11 5h5v9h-5zM18 5h2v12h-2',
  bandeira: 'M6 21V4h12l-3 4 3 4H6',
  cracha:
    'M3 6h18v12H3zM8 12a2 2 0 100-4 2 2 0 000 4M6 16c.5-1.5 1.6-2 2.5-2s2 .5 2.5 2M14 10h4M14 13h3',
  grade: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7',
  grafico: 'M4 20V11M9.5 20V5M15 20v-6M20.5 20V8',
  escudo: 'M12 3.5 20 6.5v5.5c0 4.7-3.3 7.6-8 8.5-4.7-.9-8-3.8-8-8.5V6.5z',
  chave: 'M14 10a4 4 0 10-3.5 4L9 15.5V18h2.5l2-2 1.5 1.5 1.5-1.5-1.5-1.5A4 4 0 0014 10',
  tomada: 'M9 3v5M15 3v5M6 8h12v3a6 6 0 01-12 0zM12 17v4',
  historico: 'M12 8v4l3 2M21 12a9 9 0 11-9-9M21 3v5h-5',
  /* O par do botão que troca o tema, no topo da lateral. */
  sol: 'M12 17a5 5 0 100-10 5 5 0 000 10M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5 19 19M19 5l-1.5 1.5M6.5 17.5 5 19',
  lua: 'M20 14.5A8.5 8.5 0 119.5 4a7 7 0 0010.5 10.5z',
  mais: 'M12 5v14M5 12h14',
  menos: 'M5 12h14',
  baixar: 'M12 3v12M7 10l5 5 5-5M4 20h16',
  olho: 'M2.5 12c2.7-4.8 6.4-7.5 9.5-7.5s6.8 2.7 9.5 7.5c-2.7 4.8-6.4 7.5-9.5 7.5s-6.8-2.7-9.5-7.5zM12 15a3 3 0 100-6 3 3 0 000 6z',
  sino: 'M6 10.5a6 6 0 1112 0v4l1.8 2.7H4.2L6 14.5v-4zM9.5 20a2.5 2.5 0 005 0',
  /* Perfil de carro: teto, capô e as duas rodas. */
  /*
   * De LADO, com capô, cabine, vidro e duas rodas. O de antes era de frente e
   * sem roda à vista, e a 16px lia como uma mesa — redesenhado em 25/09/2026.
   */
  veiculo:
    'M5 17H3.5a1 1 0 01-1-1v-3.3a2 2 0 01.5-1.3L6.2 8A2 2 0 017.7 7.3h7.6a2 2 0 011.5.7l2.7 3.3 1.8.5a1.5 1.5 0 011.2 1.5V16a1 1 0 01-1 1H19M9 17h6M7 19a2 2 0 100-4 2 2 0 000 4zM17 19a2 2 0 100-4 2 2 0 000 4zM3 11.3h16.5M11 7.3v4',
  /* Duas folhas sobrepostas — a de trás desenhada só onde aparece. */
  copiar: 'M9 9h10v12H9zM6 15H5V3h10v1',

  /*
   * Os três da tabela. `reticencias` são três traços de comprimento zero: com
   * `stroke-linecap: round`, que é o do componente, cada um vira um ponto
   * redondo — e não um quadrado, que é o que um `rect` daria.
   */
  reticencias: 'M6 12v.01M12 12v.01M18 12v.01',
  ordenar:
    'M7.5 4.5v15M7.5 4.5L4.5 8M7.5 4.5l3 3.5M16.5 19.5v-15M16.5 19.5l3-3.5M16.5 19.5l-3-3.5',
  filtro: 'M3.5 5h17l-6.75 7.8V19l-3.5 1.8v-8L3.5 5z',

  /* Os oito da grade de anexos. */
  nuvem:
    'M7.5 19a4.2 4.2 0 01-.3-8.4 5.6 5.6 0 0110.9-.6A3.8 3.8 0 0117 19M12 21V11.5M8.8 14.3 12 11.1l3.2 3.2',
  imagem:
    'M4 5.5h16v13H4v-13zM9 11a1.6 1.6 0 100-3.2A1.6 1.6 0 009 11M4 15.5l4.6-4.3 4.2 4M13 15.5l3-2.8 4 3.8',
  video: 'M3.5 6h13v12h-13V6zM16.5 10.5l4-2.5v8l-4-2.5',
  audio:
    'M8.5 18.5a2.4 2.4 0 100-4.8 2.4 2.4 0 000 4.8M18.5 16.5a2.4 2.4 0 100-4.8 2.4 2.4 0 000 4.8M10.9 16.1V5.4l10-2v10.7',
  tocar: 'M8 5.3l11 6.7-11 6.7V5.3z',
  expandir: 'M10 4.5H4.5v5.5M14 4.5h5.5V10M19.5 14v5.5H14M4.5 14v5.5H10',
  mover:
    'M12 3.5v17M3.5 12h17M12 3.5 9.6 6M12 3.5 14.4 6M12 20.5 9.6 18M12 20.5l2.4-2.5M3.5 12 6 9.6M3.5 12 6 14.4M20.5 12 18 9.6M20.5 12 18 14.4',
  lixeira:
    'M4.5 6.8h15M9.5 6.8V4.3h5v2.5M6.6 6.8l.9 12.9h9l.9-12.9M10.2 10.3v6.2M13.8 10.3v6.2',

  /* Os três do player de áudio. */
  pausa: 'M9.3 5v14M14.7 5v14',
  som: 'M4.5 9.4h3.3L12.6 5.4v13.2l-4.8-4H4.5V9.4zM16.1 9.6a3.4 3.4 0 010 4.8M18.7 7.2a7 7 0 010 9.6',
  mudo: 'M4.5 9.4h3.3L12.6 5.4v13.2l-4.8-4H4.5V9.4zM16.3 10.2l4.2 3.6M20.5 10.2l-4.2 3.6',
  mapa: 'M12 21s7-6.2 7-11.5a7 7 0 10-14 0C5 14.8 12 21 12 21zM12 12a2.5 2.5 0 100-5 2.5 2.5 0 000 5z',
  coroa: 'M4 18h16M4.5 18 3 8l5 4 4-7 4 7 5-4-1.5 10',
  calculadora:
    'M6 3.5h12a1 1 0 011 1v15a1 1 0 01-1 1H6a1 1 0 01-1-1v-15a1 1 0 011-1zM8 6.5h8v3H8zM8.5 13v.01M12 13v.01M15.5 13v.01M8.5 16.5v.01M12 16.5v.01M15.5 16.5v.01',
  tendencia: 'M3.5 17 9.5 11l4 4 7-7.5M15 7.5h5.5V13',
  bateria: 'M3 8h18v11H3zM6 8V5.5h3V8M15 8V5.5h3V8M6.5 13.5h3M8 12v3M14.5 13.5h3',
  pneu: 'M12 21a9 9 0 100-18 9 9 0 000 18zM12 15.5a3.5 3.5 0 100-7 3.5 3.5 0 000 7zM12 3v5.5M12 15.5V21M3 12h5.5M15.5 12H21',
  combustivel:
    'M3 21h12M4.5 9h9M13.5 21V5a2 2 0 00-2-2h-5a2 2 0 00-2 2v16M13.5 12.5h2a2 2 0 012 2v2a1.75 1.75 0 003.5 0V9.5L18 6',
  casa: 'M3 10.5 12 3l9 7.5M5 9v11.5h14V9M9.5 20.5v-6h5v6',
  taxi: 'M5 18H3.5a1 1 0 01-1-1v-3.3a2 2 0 01.5-1.3L6.2 9A2 2 0 017.7 8.3h7.6a2 2 0 011.5.7l2.7 3.3 1.8.5a1.5 1.5 0 011.2 1.5V17a1 1 0 01-1 1H19M9 18h6M7 20a2 2 0 100-4 2 2 0 000 4zM17 20a2 2 0 100-4 2 2 0 000 4zM9.5 8.3V5h5v3.3',
  cama: 'M2.5 5v15M2.5 9h17a2 2 0 012 2v9M2.5 16.5h19M7 9v7.5',
  chaveiro: 'M7.5 21a5 5 0 100-10 5 5 0 000 10zM7.5 17.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM11 12.5 20.5 3M16 7.5l2.5 2.5M18.5 5l2.5 2.5',
  garagem: 'M3 21V9.5L12 4l9 5.5V21M7 21v-8.5h10V21M7 16.5h10',
};

/**
 * O traçado de um ícone, cru — para quem desenha fora do React, como o pino
 * do `RouteMap`, que o Leaflet monta como HTML. A espessura e a cor são de
 * quem desenha; o traçado continua sendo o desta tabela.
 */
export function iconPath(nome: IconName): string {
  return STROKES[nome];
}

/** Só o `ponto` é sólido: ele é marcador de estado, não desenho. */
const FILLED = new Set<IconName>(['ponto', 'coroa']);

/**
 * Símbolo de marca de terceiro: cheio e SEM contorno. O traço da casa
 * engrossaria o desenho e fecharia os vãos que fazem a marca ser reconhecida.
 */
const BRAND = new Set<IconName>(['pix']);

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  nome: IconName;
  /** 20px na interface, 24px em destaque. */
  size?: 16 | 20 | 24;
  /** Só quando o ícone estiver sozinho, sem texto ao lado que já o explique. */
  label?: string;
}

export function Icon({ nome, size = 20, label, className, ...props }: IconProps) {
  const filled = FILLED.has(nome);
  return (
    <svg
      {...props}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={cn('shrink-0', className)}
    >
      {/*
       * A espessura vem do utilitário `traco-icone`, que lê `--traco-icone`.
       *
       * A classe vai no `path` e não no `svg`: o atributo de apresentação do
       * próprio elemento ganha do valor HERDADO do pai, e perde para uma
       * declaração CSS nele mesmo. Por isso o atributo continua aqui como
       * reserva. O token existe porque o portal declara
       * `--traco-icone: 2`: os traçados dele vieram do protótipo, que é a
       * família Lucide, desenhada para essa espessura. O token estava em
       * `portal.css` desde 08/09 e nada o lia, então os dois aplicativos
       * desenhavam com o 1.75 da interna.
       */}
      {BRAND.has(nome) ? (
        <path d={STROKES[nome]} fill="currentColor" />
      ) : (
        <path
          className="traco-icone"
          d={STROKES[nome]}
          stroke="currentColor"
          fill={filled ? 'currentColor' : 'none'}
          strokeWidth={1.75}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}
